import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Alert,
  LinearProgress,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  Collapse,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Badge,
  Avatar,
  Paper,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  CheckCircle,
  Refresh,
  ExpandMore,
  ExpandLess,
  Groups,
  LocalFireDepartment,
  PlayArrow,
  Pause,
  Timeline,
  Email,
  Close,
  Search,
  FilterList,
  VerifiedUser,
  TrendingUp,
  Speed,
  AccountCircle,
  Visibility,
  Reply,
} from '@mui/icons-material';
import warmupApi from '../../services/warmupApi';

const formatShortDateTime = (value) => {
  if (!value) return 'No activity yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No activity yet';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatRelativeTime = (value) => {
  if (!value) return 'No activity';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No activity';
  const diff = Date.now() - date.getTime();
  if (diff < 60 * 1000) return 'Just now';
  if (diff < 60 * 60 * 1000) return `${Math.max(1, Math.floor(diff / (60 * 1000)))}m ago`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / (60 * 60 * 1000))}h ago`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

/**
 * Professional Accounts Manager with Advanced Features
 * Manages SMTP accounts, groups, testing, usage analytics
 */
const AccountsManager = ({
  accounts = [],
  accountGroups = [],
  onAddAccount = () => {},
  onEditAccount = () => {},
  onDeleteAccount = () => {},
  onTestAccount = () => {},
  onCreateGroup = () => {},
  onEditGroup = () => {},
  onDeleteGroup = () => {},
  selectedAccountId = '',
  onSelectAccount = () => {},
  bulkTesting = false,
  onBulkTest = () => {},
  accountUsageToday = {},
  campaignPlan = { perAccountDaily: 0 },
  sentRecords = [],
  trackingData = [],
  inboxRecords = [],
  analyticsRefreshing = false,
  lastAnalyticsRefreshAt = null,
  onRefreshAnalytics = () => {},
  onUpdateAccountLimit = () => {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [expandedGroupId, setExpandedGroupId] = useState('');
  const [groupsExpanded, setGroupsExpanded] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [limitDrafts, setLimitDrafts] = useState({});
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [limitDialogAccount, setLimitDialogAccount] = useState(null);
  
  // Warmup states
  const [warmupStats, setWarmupStats] = useState({});
  const [warmupLoading, setWarmupLoading] = useState(false);
  const [warmupDialogOpen, setWarmupDialogOpen] = useState(false);
  const [selectedWarmupAccount, setSelectedWarmupAccount] = useState(null);

  // Load warmup stats for all accounts
  useEffect(() => {
    loadWarmupStats();
  }, [accounts]);

  const loadWarmupStats = async () => {
    try {
      const promises = accounts.map(async (account) => {
        try {
          const result = await warmupApi.getWarmupStatus(account.id);
          return { accountId: account.id, data: result.data };
        } catch (error) {
          return { accountId: account.id, data: null };
        }
      });

      const results = await Promise.all(promises);
      const stats = {};
      results.forEach(({ accountId, data }) => {
        stats[accountId] = data;
      });
      setWarmupStats(stats);
    } catch (error) {
      console.error('Error loading warmup stats:', error);
    }
  };

  const handleInitializeWarmup = async (accountId) => {
    setSelectedWarmupAccount(accountId);
    setWarmupDialogOpen(true);
  };

  const confirmInitializeWarmup = async () => {
    if (!selectedWarmupAccount) return;

    setWarmupLoading(true);
    try {
      const response = await warmupApi.initializeWarmup(selectedWarmupAccount);
      
      // Show success notification with details
      const warmupInfo = response?.data || {};
      const day = warmupInfo.current_day || 1;
      const plan = warmupInfo.plan || 30;
      const message = warmupInfo.message || '';
      
      // Check if warmup already exists or newly created
      if (message.toLowerCase().includes('already exists') || message.toLowerCase().includes('already active')) {
        alert(`ℹ️ Warmup is already active for this account!\n\nDay ${day}/${plan} - Currently warming up your email account.`);
      } else {
        alert(`✅ Email warmup started successfully!\n\nDay ${day}/${plan} - Your account will gradually send warm-up emails over the next ${plan} days.`);
      }
      
      // Force UI refresh - reload warmup stats with delay
      setWarmupDialogOpen(false);
      setSelectedWarmupAccount(null);
      await loadWarmupStats();
      
      // Double refresh to ensure UI updates
      setTimeout(() => {
        loadWarmupStats();
        setWarmupLoading(false);
      }, 300);
    } catch (error) {
      console.error('Error initializing warmup:', error);
      alert('❌ Failed to initialize warmup: ' + (error.message || 'Unknown error'));
      setWarmupLoading(false);
      setWarmupDialogOpen(false);
      setSelectedWarmupAccount(null);
    }
  };

  const handlePauseWarmup = async (accountId) => {
    setWarmupLoading(true);
    try {
      await warmupApi.pauseWarmup(accountId);
      await loadWarmupStats();
      setTimeout(() => loadWarmupStats(), 500);
      alert('⏸️ Warmup paused successfully');
    } catch (error) {
      console.error('Error pausing warmup:', error);
      alert('❌ Failed to pause warmup: ' + (error.message || 'Unknown error'));
    } finally {
      setWarmupLoading(false);
    }
  };

  const handleResumeWarmup = async (accountId) => {
    setWarmupLoading(true);
    try {
      await warmupApi.resumeWarmup(accountId);
      await loadWarmupStats();
      setTimeout(() => loadWarmupStats(), 500);
      alert('▶️ Warmup resumed successfully');
    } catch (error) {
      console.error('Error resuming warmup:', error);
      alert('❌ Failed to resume warmup: ' + (error.message || 'Unknown error'));
    } finally {
      setWarmupLoading(false);
    }
  };

  const getWarmupStatus = (accountId) => {
    const warmup = warmupStats[accountId];
    
    if (!warmup || !warmup.status) {
      return { 
        label: 'Not Started', 
        color: '#6b7280', 
        icon: <Timeline sx={{ color: '#6b7280', fontSize: 18 }} />,
        progress: null,
        status: 'not_started'
      };
    }

    const { status, current_day, plan, progress, is_complete } = warmup;

    if (is_complete) {
      return { 
        label: 'Completed', 
        color: '#16a34a', 
        icon: <LocalFireDepartment sx={{ color: '#16a34a', fontSize: 18 }} />,
        progress: 100,
        status: 'completed'
      };
    }

    if (status === 'paused') {
      return { 
        label: 'Paused', 
        color: '#f59e0b', 
        icon: <Pause sx={{ color: '#f59e0b', fontSize: 18 }} />,
        progress: progress || 0,
        status: 'paused'
      };
    }

    if (status === 'active') {
      return { 
        label: `Day ${current_day}/30`, 
        color: '#f97316', 
        icon: <LocalFireDepartment sx={{ color: '#f97316', fontSize: 18 }} />,
        progress: progress || 0,
        status: 'active'
      };
    }

    return { 
      label: 'Not Started', 
      color: '#6b7280', 
      icon: <Timeline sx={{ color: '#6b7280', fontSize: 18 }} />,
      progress: null,
      status: 'not_started'
    };
  };

  // Filter accounts by search only
  const filteredAccounts = accounts.filter((a) => {
    const matchesSearch =
      !searchTerm ||
      (a.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.fromEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.username?.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const getLimitDraftValue = (account) => {
    if (Object.prototype.hasOwnProperty.call(limitDrafts, account.id)) {
      return limitDrafts[account.id];
    }
    return String(account?.dailyLimit ?? campaignPlan.perAccountDaily ?? 100);
  };

  const commitAccountLimit = (account) => {
    const raw = getLimitDraftValue(account);
    const nextLimit = Math.max(0, Number(raw) || 0);
    if (nextLimit === Number(account?.dailyLimit ?? 0)) {
      return;
    }
    onUpdateAccountLimit(account.id, nextLimit);
  };

  const openLimitDialog = (account) => {
    setLimitDrafts((prev) => ({
      ...prev,
      [account.id]: String(account?.dailyLimit ?? campaignPlan.perAccountDaily ?? 100),
    }));
    setLimitDialogAccount(account);
    setLimitDialogOpen(true);
  };

  const handleLimitDialogSave = () => {
    if (!limitDialogAccount) return;
    commitAccountLimit(limitDialogAccount);
    setLimitDialogOpen(false);
    setLimitDialogAccount(null);
  };

  // Calculate usage stats
  const getUsagePercent = (accountId) => {
    const account = accountMap.get(accountId);
    const limit = account?.dailyLimit || campaignPlan.perAccountDaily || 100;
    const used = accountUsageToday[accountId] || 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageStatus = (accountId) => {
    const percent = getUsagePercent(accountId);
    if (percent > 90) return { color: '#f44336', label: 'Critical' };
    if (percent > 70) return { color: '#FF9800', label: 'High' };
    if (percent > 50) return { color: '#FFC107', label: 'Moderate' };
    return { color: '#4CAF50', label: 'Low' };
  };

  const accountMap = useMemo(() => {
    const map = new Map();
    accounts.forEach((acc) => map.set(acc.id, acc));
    return map;
  }, [accounts]);

  const getAccountHealth = (account) => {
    const status = account?.status || 'unknown';
    if (status === 'connected') {
      return { label: 'Working', color: '#16a34a' };
    }
    if (status === 'failed') {
      return { label: 'Not working', color: '#dc2626' };
    }
    return { label: 'Unknown', color: '#64748b' };
  };

  const accountAnalytics = useMemo(() => {
    const accountLookup = new Map();
    accounts.forEach((account) => {
      if (account?.id) {
        accountLookup.set(account.id, account);
      }
    });

    const sentLookup = new Map();
    sentRecords.forEach((record) => {
      const messageKey = record?.messageId || record?.id;
      if (messageKey) {
        sentLookup.set(`message:${messageKey}`, record);
      }
      if (record?.trackingId) {
        sentLookup.set(`tracking:${record.trackingId}`, record);
      }
    });

    const analyticsMap = new Map();
    accounts.forEach((account) => {
      const limit = account?.dailyLimit || campaignPlan.perAccountDaily || 0;
      const sentToday = accountUsageToday[account.id] || 0;
      analyticsMap.set(account.id, {
        accountId: account.id,
        sentCount: 0,
        openEvents: 0,
        openedMessages: 0,
        clickEvents: 0,
        clickedMessages: 0,
        trackedReplies: 0,
        inboxReplies: 0,
        inboxCount: 0,
        sentToday,
        dailyLimit: limit,
        remainingToday: limit > 0 ? Math.max(limit - sentToday, 0) : null,
        lastSentAt: null,
        lastOpenAt: null,
        lastReplyAt: null,
        lastInboxAt: null,
        recentActivity: [],
      });
    });

    sentRecords.forEach((record) => {
      const accountId = record?.accountId || record?.account_id;
      if (!accountId || !analyticsMap.has(accountId)) return;
      const stats = analyticsMap.get(accountId);
      const sentAt = record?.sentAt || record?.timestamp || record?.createdAt || null;
      stats.sentCount += 1;
      if (sentAt && (!stats.lastSentAt || new Date(sentAt) > new Date(stats.lastSentAt))) {
        stats.lastSentAt = sentAt;
      }
      stats.recentActivity.push({
        id: `sent-${record?.id || record?.messageId || stats.sentCount}`,
        type: 'sent',
        subject: record?.subject || '(No subject)',
        recipient: Array.isArray(record?.recipients) ? record.recipients[0] : record?.recipient || '',
        at: sentAt,
      });
    });

    trackingData.forEach((item, index) => {
      const sentRecord =
        sentLookup.get(`message:${item?.messageId || ''}`) ||
        sentLookup.get(`tracking:${item?.trackingId || ''}`) ||
        null;
      const directAccountId = item?.accountId || sentRecord?.accountId || sentRecord?.account_id || '';
      const fallbackAccount = accounts.find((account) =>
        (item?.fromEmail && (account.fromEmail === item.fromEmail || account.username === item.fromEmail)) ||
        (item?.account && (account.label === item.account || account.fromEmail === item.account))
      );
      const accountId = directAccountId || fallbackAccount?.id || '';
      if (!accountId || !analyticsMap.has(accountId)) return;

      const stats = analyticsMap.get(accountId);
      if (!sentRecord) {
        stats.sentCount += 1;
        const itemSentAt = item?.sentAt || item?.updatedAt || null;
        if (itemSentAt && (!stats.lastSentAt || new Date(itemSentAt) > new Date(stats.lastSentAt))) {
          stats.lastSentAt = itemSentAt;
        }
      }
      const openCount = Number(item?.openCount ?? item?.totalOpens ?? 0) || 0;
      const clickCount = Number(item?.clickCount ?? 0) || 0;
      const opened = Boolean(item?.opened || openCount > 0 || item?.lastOpenedAt || item?.openedAt);
      const clicked = Boolean(item?.clicked || clickCount > 0 || item?.clickedAt || item?.lastClickedAt);
      const replied = Boolean(item?.replied);
      const openedAt = item?.openedAt || item?.lastOpenedAt || null;
      const clickedAt = item?.clickedAt || item?.lastClickedAt || null;
      const replyAt = item?.repliedAt || item?.lastReplyAt || null;
      const subject = item?.subject || sentRecord?.subject || '(No subject)';
      const recipient = item?.recipient || sentRecord?.recipient || sentRecord?.recipients?.[0] || '';

      stats.openEvents += openCount;
      stats.clickEvents += clickCount;
      if (opened) {
        stats.openedMessages += 1;
        if (openedAt && (!stats.lastOpenAt || new Date(openedAt) > new Date(stats.lastOpenAt))) {
          stats.lastOpenAt = openedAt;
        }
      }
      if (clicked) {
        stats.clickedMessages += 1;
      }
      if (replied) {
        stats.trackedReplies += 1;
        if (replyAt && (!stats.lastReplyAt || new Date(replyAt) > new Date(stats.lastReplyAt))) {
          stats.lastReplyAt = replyAt;
        }
      }

      if (opened) {
        stats.recentActivity.push({
          id: `open-${item?.id || item?.messageId || index}`,
          type: 'open',
          subject,
          recipient,
          at: openedAt,
          count: openCount,
        });
      }
      if (clicked) {
        stats.recentActivity.push({
          id: `click-${item?.id || item?.messageId || index}`,
          type: 'click',
          subject,
          recipient,
          at: clickedAt,
          count: clickCount,
        });
      }
      if (replied) {
        stats.recentActivity.push({
          id: `reply-${item?.id || item?.messageId || index}`,
          type: 'reply',
          subject,
          recipient,
          at: replyAt,
        });
      }
    });

    inboxRecords.forEach((message, index) => {
      const accountId = message?.accountId || '';
      if (!accountId || !analyticsMap.has(accountId)) return;
      const stats = analyticsMap.get(accountId);
      const timestamp = message?.timestamp || message?.date || null;
      stats.inboxCount += 1;
      stats.inboxReplies += 1;
      if (timestamp && (!stats.lastInboxAt || new Date(timestamp) > new Date(stats.lastInboxAt))) {
        stats.lastInboxAt = timestamp;
      }
      stats.recentActivity.push({
        id: `inbox-${message?.id || index}`,
        type: 'inbox',
        subject: message?.subject || '(No subject)',
        recipient: message?.from || '',
        at: timestamp,
      });
    });

    analyticsMap.forEach((stats) => {
      stats.replyCount = Math.max(stats.trackedReplies, stats.inboxReplies);
      stats.openRate = stats.sentCount > 0 ? Math.round((stats.openedMessages / stats.sentCount) * 100) : 0;
      stats.clickRate = stats.sentCount > 0 ? Math.round((stats.clickedMessages / stats.sentCount) * 100) : 0;
      stats.recentActivity = stats.recentActivity
        .filter((item) => item.at)
        .sort((a, b) => new Date(b.at) - new Date(a.at))
        .slice(0, 6);
    });

    return analyticsMap;
  }, [accounts, accountUsageToday, campaignPlan.perAccountDaily, inboxRecords, sentRecords, trackingData]);

  const analyticsOverview = useMemo(() => {
    const rows = Array.from(accountAnalytics.values());
    const engagedAccounts = rows.filter((item) => item.openedMessages > 0).length;
    const activeSenders = rows.filter((item) => item.sentCount > 0).length;
    const totalSent = rows.reduce((sum, item) => sum + item.sentCount, 0);
    const totalOpened = rows.reduce((sum, item) => sum + item.openedMessages, 0);
    const totalReplies = rows.reduce((sum, item) => sum + item.replyCount, 0);
    const bestAccount = rows.reduce((best, current) => {
      if (!best) return current;
      if (current.openRate > best.openRate) return current;
      if (current.openRate === best.openRate && current.sentCount > best.sentCount) return current;
      return best;
    }, null);

    return {
      engagedAccounts,
      activeSenders,
      totalSent,
      totalOpened,
      totalReplies,
      bestAccount,
    };
  }, [accountAnalytics]);

  const visibleIds = filteredAccounts.map((a) => a.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedAccounts.includes(id));
  const someVisibleSelected =
    visibleIds.some((id) => selectedAccounts.includes(id)) && !allVisibleSelected;

  const toggleSelection = (id) => {
    setSelectedAccounts((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectAllVisible = () => {
    setSelectedAccounts(filteredAccounts.map((a) => a.id));
  };

  const clearSelection = () => {
    setSelectedAccounts([]);
  };

  const handleCreateGroup = () => {
    if (!selectedAccounts.length) return;
    onCreateGroup(selectedAccounts);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2.5 }}>
      {/* Premium Header Section */}
      <Card 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          },
        }}
      >
        <CardContent sx={{ p: isMobile ? 2 : 3, position: 'relative', zIndex: 1 }}>
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={isMobile ? 2 : 2}
            >
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Avatar sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.2)', 
                    backdropFilter: 'blur(10px)',
                    width: isMobile ? 40 : 48, 
                    height: isMobile ? 40 : 48,
                  }}>
                    <Email sx={{ color: 'white', fontSize: isMobile ? 24 : 28 }} />
                  </Avatar>
                  <Box>
                    <Typography 
                      variant={isMobile ? 'h6' : 'h5'} 
                      fontWeight={700} 
                      sx={{ 
                        color: 'white',
                        textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    >
                      Email Accounts Manager
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                      }}
                    >
                      Manage SMTP accounts, groups, and email warmup
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              
              <Stack 
                direction={{ xs: 'row', sm: 'row' }} 
                spacing={1.5} 
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                <Button
                  size={isMobile ? 'medium' : 'large'}
                  variant="outlined"
                  startIcon={analyticsRefreshing ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Visibility />}
                  onClick={onRefreshAnalytics}
                  sx={{ 
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    fontWeight: 600,
                    backdropFilter: 'blur(10px)',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.18)',
                    },
                  }}
                >
                  {analyticsRefreshing ? 'Refreshing...' : 'Refresh Insights'}
                </Button>
                <Button
                  size={isMobile ? 'medium' : 'large'}
                  variant="contained"
                  startIcon={<Add />}
                  onClick={onAddAccount}
                  fullWidth={isMobile}
                  sx={{ 
                    bgcolor: 'white',
                    color: '#667eea',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.95)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isMobile ? 'Add Account' : 'Add New Account'}
                </Button>
                <Button
                  size={isMobile ? 'medium' : 'large'}
                  variant="outlined"
                  startIcon={bulkTesting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Refresh />}
                  onClick={onBulkTest}
                  disabled={bulkTesting || filteredAccounts.length === 0}
                  sx={{ 
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    fontWeight: 600,
                    backdropFilter: 'blur(10px)',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.2)',
                    },
                  }}
                >
                  {bulkTesting ? 'Testing...' : 'Test All'}
                </Button>
              </Stack>
            </Stack>

            {/* Search Bar & Selection Controls */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
              <TextField
                size="small"
                placeholder="Search accounts by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ 
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'white',
                    },
                    '& input::placeholder': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      opacity: 1,
                    },
                  },
                }}
                variant="outlined"
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'rgba(255,255,255,0.7)' }} />,
                  endAdornment: searchTerm ? (
                    <IconButton size="small" onClick={() => setSearchTerm('')} edge="end" sx={{ color: 'white' }}>
                      <Close fontSize="small" />
                    </IconButton>
                  ) : null,
                }}
              />
              
              {selectedAccounts.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Badge badgeContent={selectedAccounts.length} color="error" max={999}>
                    <Chip
                      icon={<CheckCircle />}
                      label={`${selectedAccounts.length} Selected`}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(10px)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.3)',
                      }}
                    />
                  </Badge>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={clearSelection}
                    sx={{ 
                      borderColor: 'rgba(255,255,255,0.5)',
                      color: 'white',
                      '&:hover': {
                        borderColor: 'white',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Groups />}
                    onClick={handleCreateGroup}
                    sx={{ 
                      bgcolor: 'white',
                      color: '#667eea',
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.95)',
                      },
                    }}
                  >
                    Create Group
                  </Button>
                </Stack>
              )}
            </Stack>
          </Stack>

          {/* Stats Row */}
          <Stack 
            direction="row" 
            spacing={isMobile ? 1 : 2} 
            sx={{ mt: 3 }} 
            flexWrap="wrap"
            useFlexGap
          >
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                flex: { xs: '0 0 calc(50% - 8px)', sm: 'auto' },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <AccountCircle sx={{ color: 'white', fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                    {accounts.length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Total Accounts
                  </Typography>
                </Box>
              </Stack>
            </Paper>
            
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                flex: { xs: '0 0 calc(50% - 8px)', sm: 'auto' },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <VerifiedUser sx={{ color: '#4ade80', fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                    {accounts.filter(a => a.status === 'connected').length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Active
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                flex: { xs: '0 0 calc(50% - 8px)', sm: 'auto' },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Groups sx={{ color: '#fbbf24', fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                    {accountGroups.length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Groups
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                flex: { xs: '0 0 calc(50% - 8px)', sm: 'auto' },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterList sx={{ color: '#a78bfa', fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                    {filteredAccounts.length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Filtered
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                flex: { xs: '0 0 calc(50% - 8px)', sm: 'auto' },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Visibility sx={{ color: '#93c5fd', fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                    {analyticsOverview.totalOpened}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Opened Emails
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                flex: { xs: '0 0 calc(50% - 8px)', sm: 'auto' },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Reply sx={{ color: '#fbbf24', fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                    {analyticsOverview.totalReplies}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Replies / Inbox
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={2} flexWrap="wrap">
            <Chip
              icon={<TrendingUp />}
              label={`${analyticsOverview.activeSenders} active senders`}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'white',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            />
            <Chip
              icon={<Visibility />}
              label={`${analyticsOverview.engagedAccounts} accounts with opens`}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'white',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            />
            <Chip
              label={
                analyticsOverview.bestAccount
                  ? `Best open rate: ${accounts.find((account) => account.id === analyticsOverview.bestAccount.accountId)?.fromEmail || analyticsOverview.bestAccount.accountId} (${analyticsOverview.bestAccount.openRate}%)`
                  : 'Best open rate will appear after tracked sends'
              }
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'white',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.2)',
                maxWidth: '100%',
              }}
            />
            <Chip
              label={lastAnalyticsRefreshAt ? `Insights synced ${formatRelativeTime(lastAnalyticsRefreshAt)}` : 'Insights waiting for first sync'}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'white',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Groups Section - Collapsible */}
      <Card 
        elevation={0}
        sx={{ 
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.12) 0%, rgba(245, 158, 11, 0.12) 100%)'
            : 'linear-gradient(135deg, rgba(251, 191, 36, 0.08) 0%, rgba(245, 158, 11, 0.08) 100%)',
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(251, 191, 36, 0.4)' : 'rgba(251, 191, 36, 0.3)',
          borderRadius: 2,
          transition: 'all 0.3s ease',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Stack 
            direction="row" 
            spacing={1.5} 
            alignItems="center"
            onClick={() => setGroupsExpanded(!groupsExpanded)}
            sx={{
              p: isMobile ? 1.5 : 2,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'rgba(251, 191, 36, 0.05)',
              },
              transition: 'background-color 0.2s ease',
            }}
          >
            <Avatar sx={{ bgcolor: '#fbbf24', width: 36, height: 36 }}>
              <Groups fontSize="small" sx={{ color: 'white' }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={700}>
                Account Groups {accountGroups.length > 0 && `(${accountGroups.length})`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {groupsExpanded ? 'Click to collapse' : 'Click to expand and manage groups'}
              </Typography>
            </Box>
            <Chip 
              label={accountGroups.length} 
              size="small"
              sx={{ 
                bgcolor: '#fbbf24',
                color: 'white',
                fontWeight: 700,
              }}
            />
            <IconButton size="small" sx={{ color: '#fbbf24' }}>
              {groupsExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Stack>
          
          <Collapse in={groupsExpanded}>
            <Box sx={{ p: isMobile ? 1.5 : 2, pt: 0 }}>
            <Divider sx={{ mb: 2 }} />
            
            {accountGroups.length === 0 ? (
              <Paper
                elevation={0}
                sx={{ 
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'rgba(251, 191, 36, 0.05)',
                  border: '2px dashed',
                  borderColor: 'rgba(251, 191, 36, 0.3)',
                  borderRadius: 2,
                }}
              >
                <Groups sx={{ fontSize: 48, color: 'rgba(251, 191, 36, 0.4)', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  No groups created yet
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Select accounts above and click "Create Group" to get started
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={1.5}>
                {accountGroups.map((group) => (
                  <Paper
                    key={group.id}
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: 'rgba(251, 191, 36, 0.3)',
                      borderRadius: 2,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#fbbf24',
                        boxShadow: '0 4px 12px rgba(251, 191, 36, 0.15)',
                      },
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                      sx={{
                        p: 1.5,
                        bgcolor: 'white',
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                        <Avatar sx={{ bgcolor: '#fbbf24', width: 36, height: 36 }}>
                          <Groups fontSize="small" sx={{ color: 'white' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700}>
                            {group.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {group.accountIds?.length || 0} accounts in this group
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant={expandedGroupId === group.id ? 'contained' : 'outlined'}
                          onClick={() => setExpandedGroupId(expandedGroupId === group.id ? '' : group.id)}
                          startIcon={expandedGroupId === group.id ? <ExpandLess /> : <ExpandMore />}
                          sx={{
                            ...(expandedGroupId === group.id && {
                              bgcolor: '#fbbf24',
                              '&:hover': { bgcolor: '#f59e0b' },
                            }),
                          }}
                        >
                          {expandedGroupId === group.id ? 'Hide' : 'View'}
                        </Button>
                        <Button 
                          size="small"
                          variant="outlined" 
                          onClick={() => onEditGroup(group)}
                          startIcon={<Edit />}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="small"
                          color="error" 
                          variant="outlined" 
                          onClick={() => onDeleteGroup(group.id)}
                          startIcon={<Delete />}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </Stack>
                    
                    <Collapse in={expandedGroupId === group.id} unmountOnExit>
                      <Box sx={{ p: 1.5, bgcolor: 'rgba(251, 191, 36, 0.03)' }}>
                        <Stack spacing={1}>
                          {(group.accountIds || []).map((id) => {
                            const acc = accountMap.get(id);
                            if (!acc) return null;
                            const health = getAccountHealth(acc);
                            return (
                              <Paper
                                key={id}
                                elevation={0}
                                sx={{
                                  p: 1.5,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1.5,
                                  bgcolor: 'white',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    borderColor: '#fbbf24',
                                    transform: 'translateX(4px)',
                                  },
                                }}
                              >
                                <Stack
                                  direction={{ xs: 'column', sm: 'row' }}
                                  spacing={1}
                                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                                  justifyContent="space-between"
                                >
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" fontWeight={700} noWrap>
                                      {acc.label || acc.fromEmail || acc.username}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {acc.fromEmail || acc.username}
                                    </Typography>
                                  </Box>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                      size="small"
                                      label={health.label}
                                      sx={{ 
                                        bgcolor: `${health.color}15`,
                                        color: health.color,
                                        fontWeight: 600,
                                        border: '1px solid',
                                        borderColor: `${health.color}40`,
                                      }}
                                    />
                                    <Button 
                                      size="small" 
                                      variant="outlined" 
                                      onClick={() => onTestAccount(acc)}
                                      startIcon={<Refresh />}
                                    >
                                      Test
                                    </Button>
                                  </Stack>
                                </Stack>
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Box>
                    </Collapse>
                  </Paper>
                ))}
              </Stack>
            )}
            
            <Divider />
            
            <Alert 
              severity="info" 
              sx={{ 
                borderRadius: 2,
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'rgba(59, 130, 246, 0.15)'
                  : 'rgba(59, 130, 246, 0.08)',
                border: '1px solid',
                borderColor: 'rgba(59, 130, 246, 0.3)',
              }}
              icon={<TrendingUp />}
            >
              <Typography variant="body2" fontWeight={600}>
                Daily Email Limit: {campaignPlan.perAccountDaily || 0} emails per account
              </Typography>
            </Alert>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      <Card
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'rgba(16, 185, 129, 0.18)',
          borderRadius: 2,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(59, 130, 246, 0.10))'
            : 'linear-gradient(135deg, rgba(16, 185, 129, 0.06), rgba(59, 130, 246, 0.05))',
        }}
      >
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Account Performance Insights
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Har sender account ka sent, opened, reply aur inbox activity yahan se monitor karein.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={`Tracked sent ${analyticsOverview.totalSent}`} color="primary" variant="outlined" />
              <Chip label={`Opened ${analyticsOverview.totalOpened}`} color="success" variant="outlined" />
              <Chip label={`Replies ${analyticsOverview.totalReplies}`} color="warning" variant="outlined" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Accounts List/Table */}
      {filteredAccounts.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ 
            p: 4,
            textAlign: 'center',
            bgcolor: 'rgba(102, 126, 234, 0.05)',
            border: '2px dashed',
            borderColor: 'rgba(102, 126, 234, 0.3)',
            borderRadius: 2,
          }}
        >
          <Email sx={{ fontSize: 64, color: 'rgba(102, 126, 234, 0.3)', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} color="text.secondary" gutterBottom>
            {accounts.length === 0 ? 'No Accounts Yet' : 'No Matching Accounts'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {accounts.length === 0
              ? 'Add your first SMTP account to begin sending emails'
              : 'Try adjusting your search or filter criteria'}
          </Typography>
          {accounts.length === 0 && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={onAddAccount}
              sx={{ 
                mt: 1,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                },
              }}
            >
              Add Your First Account
            </Button>
          )}
        </Paper>
      ) : (
        <>
          {/* Desktop View - Modern Table */}
          {!isMobile && (
            <Card
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'rgba(102, 126, 234, 0.2)',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow 
                      sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      }}
                    >
                      <TableCell padding="checkbox" sx={{ borderBottom: 'none' }}>
                        <Checkbox
                          size="small"
                          checked={allVisibleSelected}
                          indeterminate={someVisibleSelected}
                          onChange={(e) => (e.target.checked ? selectAllVisible() : clearSelection())}
                          sx={{
                            color: 'rgba(255,255,255,0.7)',
                            '&.Mui-checked': { color: 'white' },
                            '&.MuiCheckbox-indeterminate': { color: 'white' },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', borderBottom: 'none' }}>
                        Account Details
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', borderBottom: 'none' }}>
                        Provider
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', borderBottom: 'none' }}>
                        Usage Today
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', borderBottom: 'none' }}>
                        Performance
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', borderBottom: 'none' }}>
                        Warmup Status
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', borderBottom: 'none' }}>
                        Health
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', borderBottom: 'none' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAccounts.map((account, idx) => {
                      const usagePercent = getUsagePercent(account.id);
                      const usageStatus = getUsageStatus(account.id);
                      const used = accountUsageToday[account.id] || 0;
                      const limit = account.dailyLimit || campaignPlan.perAccountDaily || 100;
                      const isSelected = selectedAccounts.includes(account.id);
                      const isExpanded = expandedAccountId === account.id;
                      const analytics = accountAnalytics.get(account.id) || {
                        sentCount: 0,
                        openedMessages: 0,
                        clickEvents: 0,
                        replyCount: 0,
                        inboxCount: 0,
                        openRate: 0,
                        clickRate: 0,
                        lastOpenAt: null,
                        lastSentAt: null,
                        lastReplyAt: null,
                        remainingToday: null,
                        recentActivity: [],
                      };

                      return (
                        <React.Fragment key={account.id}>
                        <TableRow
                          sx={{
                            backgroundColor: selectedAccountId === account.id 
                              ? 'rgba(102, 126, 234, 0.08)' 
                              : idx % 2 === 0 ? 'white' : 'rgba(249, 250, 251, 0.5)',
                            borderLeft: selectedAccountId === account.id ? '4px solid #667eea' : '4px solid transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              backgroundColor: 'rgba(102, 126, 234, 0.05)',
                              transform: 'translateX(4px)',
                              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.1)',
                            },
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={() => toggleSelection(account.id)}
                              sx={{
                                '&.Mui-checked': { color: '#667eea' },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar 
                                sx={{ 
                                  bgcolor: selectedAccountId === account.id ? '#667eea' : '#e0e7ff',
                                  width: 36,
                                  height: 36,
                                }}
                              >
                                <Email 
                                  fontSize="small" 
                                  sx={{ color: selectedAccountId === account.id ? 'white' : '#667eea' }} 
                                />
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={700}>
                                  {account.label || account.fromEmail || account.username}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {account.fromEmail || account.username}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={account.provider || 'SMTP'}
                              sx={{
                                bgcolor: '#e0e7ff',
                                color: '#667eea',
                                fontWeight: 600,
                                border: '1px solid',
                                borderColor: '#c7d2fe',
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Stack spacing={0.5} alignItems="center">
                              <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                                {used}/{limit}
                              </Typography>
                              <Box sx={{ width: 80 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={usagePercent}
                                  sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                    '& .MuiLinearProgress-bar': {
                                      background: `linear-gradient(90deg, ${usageStatus.color}, ${usageStatus.color}dd)`,
                                      borderRadius: 3,
                                    },
                                  }}
                                />
                              </Box>
                              <Chip
                                size="small"
                                label={usageStatus.label}
                                sx={{ 
                                  height: 18,
                                  fontSize: '0.65rem',
                                  bgcolor: `${usageStatus.color}15`,
                                  color: usageStatus.color,
                                  fontWeight: 600,
                                  border: '1px solid',
                                  borderColor: `${usageStatus.color}40`,
                                }}
                              />
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<Speed fontSize="small" />}
                                onClick={() => openLimitDialog(account)}
                                sx={{
                                  minHeight: 24,
                                  px: 0.75,
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  color: '#475569',
                                }}
                              >
                                Limit {limit}
                              </Button>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <Stack spacing={0.5} alignItems="center">
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center">
                                <Chip size="small" label={`Sent ${analytics.sentCount}`} variant="outlined" />
                                <Chip size="small" label={`Opened ${analytics.openedMessages}`} color={analytics.openedMessages ? 'success' : 'default'} variant="outlined" />
                                <Chip size="small" label={`Replies ${analytics.replyCount}`} color={analytics.replyCount ? 'warning' : 'default'} variant="outlined" />
                              </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Open rate {analytics.openRate}%{analytics.lastOpenAt ? ` • ${formatRelativeTime(analytics.lastOpenAt)}` : ''}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            {warmupLoading ? (
                              <CircularProgress size={24} sx={{ color: '#667eea' }} />
                            ) : (
                              <Stack spacing={0.75} alignItems="center">
                                {(() => {
                                  const warmup = getWarmupStatus(account.id);
                                  return (
                                    <>
                                      <Stack direction="row" spacing={0.75} alignItems="center">
                                        {warmup.icon}
                                        <Chip
                                          size="small"
                                          label={warmup.label}
                                          sx={{ 
                                            bgcolor: `${warmup.color}15`,
                                            color: warmup.color,
                                            fontSize: '0.75rem',
                                            fontWeight: 700,
                                            border: '1px solid',
                                            borderColor: `${warmup.color}40`,
                                          }}
                                        />
                                      </Stack>
                                      {warmup.progress !== null && (
                                        <Box sx={{ width: 100 }}>
                                          <LinearProgress
                                            variant="determinate"
                                            value={warmup.progress}
                                            sx={{
                                              height: 6,
                                              borderRadius: 3,
                                              backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                              '& .MuiLinearProgress-bar': {
                                                background: `linear-gradient(90deg, ${warmup.color}, ${warmup.color}dd)`,
                                                borderRadius: 3,
                                              },
                                            }}
                                          />
                                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: warmup.color, fontWeight: 600 }}>
                                            {warmup.progress}%
                                          </Typography>
                                        </Box>
                                      )}
                                      {warmup.status === 'not_started' && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<LocalFireDepartment />}
                                          onClick={() => handleInitializeWarmup(account.id)}
                                          sx={{ 
                                            fontSize: '0.7rem',
                                            borderColor: '#f97316',
                                            color: '#f97316',
                                            '&:hover': {
                                              borderColor: '#ea580c',
                                              bgcolor: 'rgba(249, 115, 22, 0.08)',
                                            },
                                          }}
                                        >
                                        Start Warmup
                                      </Button>
                                    )}
                                    {warmup.status === 'active' && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Pause />}
                                        onClick={() => handlePauseWarmup(account.id)}
                                        sx={{ 
                                          fontSize: '0.7rem',
                                          borderColor: '#f59e0b',
                                          color: '#f59e0b',
                                          '&:hover': {
                                            borderColor: '#d97706',
                                            bgcolor: 'rgba(245, 158, 11, 0.08)',
                                          },
                                        }}
                                      >
                                        Pause
                                      </Button>
                                    )}
                                    {warmup.status === 'paused' && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<PlayArrow />}
                                        onClick={() => handleResumeWarmup(account.id)}
                                        sx={{ 
                                          fontSize: '0.7rem',
                                          borderColor: '#16a34a',
                                          color: '#16a34a',
                                          '&:hover': {
                                            borderColor: '#15803d',
                                            bgcolor: 'rgba(22, 163, 74, 0.08)',
                                          },
                                        }}
                                      >
                                        Resume
                                      </Button>
                                    )}
                                  </>
                                );
                              })()}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={usageStatus.label}
                            icon={usageStatus.label === 'Low' ? <CheckCircle sx={{ fontSize: 14 }} /> : undefined}
                            sx={{ 
                              bgcolor: `${usageStatus.color}15`,
                              color: usageStatus.color,
                              fontWeight: 700,
                              border: '1px solid',
                              borderColor: `${usageStatus.color}40`,
                            }}
                          />
                        </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Use this account" arrow>
                                <IconButton 
                                  size="small" 
                                  onClick={() => onSelectAccount(account.id)}
                                  sx={{ 
                                    bgcolor: selectedAccountId === account.id ? '#16a34a20' : 'transparent',
                                    '&:hover': { bgcolor: '#16a34a30' },
                                  }}
                                >
                                  <CheckCircle
                                    fontSize="small"
                                    sx={{ color: selectedAccountId === account.id ? '#16a34a' : '#94a3b8' }}
                                  />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Test connection" arrow>
                                <IconButton 
                                  size="small" 
                                  onClick={() => onTestAccount(account.id)}
                                  sx={{ '&:hover': { bgcolor: '#667eea20' } }}
                                >
                                  <Refresh fontSize="small" sx={{ color: '#667eea' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit account" arrow>
                                <IconButton 
                                  size="small" 
                                  onClick={() => onEditAccount(account)}
                                  sx={{ '&:hover': { bgcolor: '#3b82f620' } }}
                                >
                                  <Edit fontSize="small" sx={{ color: '#3b82f6' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete account" arrow>
                                <IconButton 
                                  size="small"
                                  onClick={() => onDeleteAccount(account.id)}
                                  sx={{ '&:hover': { bgcolor: '#dc262620' } }}
                                >
                                  <Delete fontSize="small" sx={{ color: '#dc2626' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={isExpanded ? 'Hide details' : 'View details'} arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => setExpandedAccountId(isExpanded ? null : account.id)}
                                  sx={{ '&:hover': { bgcolor: '#0f172a10' } }}
                                >
                                  {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} sx={{ p: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'rgba(102,126,234,0.03)' }}>
                              <Stack spacing={2}>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  <Chip label={`Sent ${analytics.sentCount}`} color="primary" variant="outlined" />
                                  <Chip label={`Opened ${analytics.openedMessages}`} color="success" variant="outlined" />
                                  <Chip label={`Clicks ${analytics.clickEvents}`} color="info" variant="outlined" />
                                  <Chip label={`Replies ${analytics.replyCount}`} color="warning" variant="outlined" />
                                  <Chip label={`Inbox ${analytics.inboxCount}`} variant="outlined" />
                                  <Chip label={`Daily limit ${account.dailyLimit || 0}`} variant="outlined" />
                                  <Chip label={`Open rate ${analytics.openRate}%`} color={analytics.openRate >= 25 ? 'success' : 'default'} variant="outlined" />
                                  <Chip label={`Click rate ${analytics.clickRate}%`} color={analytics.clickRate >= 5 ? 'info' : 'default'} variant="outlined" />
                                  <Chip label={`Remaining ${analytics.remainingToday === null ? 'Unlimited' : analytics.remainingToday}` } variant="outlined" />
                                </Stack>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Speed fontSize="small" />}
                                  onClick={() => openLimitDialog(account)}
                                  sx={{ alignSelf: 'flex-start' }}
                                >
                                  Edit sending limit
                                </Button>
                                <Typography variant="body2" color="text.secondary">
                                  Last sent: {formatShortDateTime(analytics.lastSentAt)} • Last open: {formatShortDateTime(analytics.lastOpenAt)} • Last reply: {formatShortDateTime(analytics.lastReplyAt)}
                                </Typography>
                                {analytics.recentActivity.length > 0 ? (
                                  <Stack spacing={1}>
                                    {analytics.recentActivity.map((activity) => (
                                      <Paper
                                        key={activity.id}
                                        elevation={0}
                                        sx={{
                                          p: 1.25,
                                          borderRadius: 1.5,
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          bgcolor: 'background.paper',
                                        }}
                                      >
                                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                                          <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                                              {activity.type}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600} noWrap>
                                              {activity.subject}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                              {activity.recipient || 'No recipient'}
                                            </Typography>
                                          </Box>
                                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                            {formatShortDateTime(activity.at)}
                                          </Typography>
                                        </Stack>
                                      </Paper>
                                    ))}
                                  </Stack>
                                ) : (
                                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    Is account ke liye tracked sending ya engagement activity abhi available nahi hai.
                                  </Alert>
                                )}
                              </Stack>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            </Card>
          )}

          {/* Mobile View - Modern Cards */}
          {isMobile && (
            <Stack spacing={1.5}>
              {filteredAccounts.map((account, idx) => {
                const usagePercent = getUsagePercent(account.id);
                const usageStatus = getUsageStatus(account.id);
                const used = accountUsageToday[account.id] || 0;
                const limit = account.dailyLimit || campaignPlan.perAccountDaily || 100;
                const isExpanded = expandedAccountId === account.id;
                const isSelected = selectedAccounts.includes(account.id);
                const warmup = getWarmupStatus(account.id);
                const analytics = accountAnalytics.get(account.id) || {
                  sentCount: 0,
                  openedMessages: 0,
                  clickEvents: 0,
                  replyCount: 0,
                  inboxCount: 0,
                  openRate: 0,
                  clickRate: 0,
                  recentActivity: [],
                  lastSentAt: null,
                  lastOpenAt: null,
                  lastReplyAt: null,
                  remainingToday: null,
                };

                return (
                  <Paper
                    key={account.id}
                    elevation={0}
                    sx={{
                      background: selectedAccountId === account.id 
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)' 
                        : 'white',
                      border: '1px solid',
                      borderColor: selectedAccountId === account.id ? '#667eea' : 'rgba(102, 126, 234, 0.2)',
                      borderRadius: 2,
                      borderLeft: selectedAccountId === account.id ? '4px solid #667eea' : '4px solid transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#667eea',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleSelection(account.id)}
                          sx={{
                            p: 0,
                            '&.Mui-checked': { color: '#667eea' },
                          }}
                        />
                        
                        <Avatar 
                          sx={{ 
                            bgcolor: selectedAccountId === account.id ? '#667eea' : '#e0e7ff',
                            width: 44,
                            height: 44,
                          }}
                        >
                          <Email sx={{ color: selectedAccountId === account.id ? 'white' : '#667eea' }} />
                        </Avatar>
                        
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography fontWeight={700} variant="subtitle2" noWrap>
                              {account.label || account.fromEmail || account.username}
                            </Typography>
                            {selectedAccountId === account.id && (
                              <Chip 
                                size="small" 
                                label="Active" 
                                sx={{ 
                                  height: 20,
                                  fontSize: '0.65rem',
                                  bgcolor: '#16a34a20',
                                  color: '#16a34a',
                                  fontWeight: 700,
                                }}
                              />
                            )}
                          </Stack>
                          
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {account.fromEmail || account.username}
                          </Typography>
                          
                          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
                            <Chip 
                              size="small" 
                              label={account.provider || 'SMTP'} 
                              sx={{ 
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: '#e0e7ff',
                                color: '#667eea',
                                fontWeight: 600,
                                border: '1px solid #c7d2fe',
                              }}
                            />
                            <Chip
                              size="small"
                              label={usageStatus.label}
                              sx={{ 
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: `${usageStatus.color}15`,
                                color: usageStatus.color,
                                fontWeight: 700,
                                border: '1px solid',
                                borderColor: `${usageStatus.color}40`,
                              }}
                            />
                            <Chip
                              size="small"
                              icon={warmup.icon}
                              label={warmup.label}
                              sx={{ 
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: `${warmup.color}15`,
                                color: warmup.color,
                                fontWeight: 700,
                                border: '1px solid',
                                borderColor: `${warmup.color}40`,
                              }}
                            />
                            <Chip
                              size="small"
                              icon={<Visibility sx={{ fontSize: 14 }} />}
                              label={`${analytics.openedMessages}/${analytics.sentCount} opened`}
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: analytics.openedMessages ? '#10b98115' : '#f8fafc',
                                color: analytics.openedMessages ? '#10b981' : '#64748b',
                                fontWeight: 700,
                                border: '1px solid',
                                borderColor: analytics.openedMessages ? '#10b98140' : '#cbd5e1',
                              }}
                            />
                            <Chip
                              size="small"
                              label={`Limit ${account.dailyLimit || 0}`}
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: '#f8fafc',
                                color: '#334155',
                                fontWeight: 700,
                                border: '1px solid #cbd5e1',
                              }}
                            />
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<Speed fontSize="small" />}
                              onClick={() => openLimitDialog(account)}
                              sx={{
                                minHeight: 22,
                                px: 0.5,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                color: '#475569',
                              }}
                            >
                              Edit
                            </Button>
                          </Stack>
                          
                          {/* Usage Progress */}
                          <Box sx={{ mb: 1 }}>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Daily Usage
                              </Typography>
                              <Typography variant="caption" fontWeight={700} sx={{ color: usageStatus.color }}>
                                {used}/{limit}
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={usagePercent}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                '& .MuiLinearProgress-bar': {
                                  background: `linear-gradient(90deg, ${usageStatus.color}, ${usageStatus.color}dd)`,
                                  borderRadius: 4,
                                },
                              }}
                            />
                          </Box>
                          
                          {/* Warmup Progress */}
                          {warmup.progress !== null && (
                            <Box>
                              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                  Warmup Progress
                                </Typography>
                                <Typography variant="caption" fontWeight={700} sx={{ color: warmup.color }}>
                                  {warmup.progress}%
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={warmup.progress}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                  '& .MuiLinearProgress-bar': {
                                    background: `linear-gradient(90deg, ${warmup.color}, ${warmup.color}dd)`,
                                    borderRadius: 4,
                                  },
                                }}
                              />
                            </Box>
                          )}

                          <Stack direction="row" spacing={0.75} mt={1.25} flexWrap="wrap">
                            <Chip size="small" label={`Sent ${analytics.sentCount}`} variant="outlined" />
                            <Chip size="small" label={`Clicks ${analytics.clickEvents}`} variant="outlined" />
                            <Chip size="small" label={`Inbox ${analytics.inboxCount}`} variant="outlined" />
                            <Chip size="small" label={`Open rate ${analytics.openRate}%`} color={analytics.openRate >= 25 ? 'success' : 'default'} variant="outlined" />
                          </Stack>
                        </Box>
                        
                        <Stack direction="column" spacing={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedAccountId(isExpanded ? null : account.id)}
                            sx={{ 
                              bgcolor: isExpanded ? '#667eea20' : 'transparent',
                              '&:hover': { bgcolor: '#667eea30' },
                            }}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => onSelectAccount(account.id)}
                            sx={{ 
                              bgcolor: selectedAccountId === account.id ? '#16a34a20' : 'transparent',
                              '&:hover': { bgcolor: '#16a34a30' },
                            }}
                          >
                            <CheckCircle sx={{ color: selectedAccountId === account.id ? '#16a34a' : '#94a3b8' }} />
                          </IconButton>
                        </Stack>
                      </Stack>

                      <Collapse in={isExpanded}>
                        <Divider sx={{ my: 1.5 }} />
                        <Stack spacing={1.5}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: 'rgba(102,126,234,0.04)',
                              border: '1px solid rgba(102,126,234,0.12)',
                            }}
                          >
                            <Stack spacing={1}>
                              <Typography variant="subtitle2" fontWeight={700}>
                                Account Analytics
                              </Typography>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap">
                                <Chip size="small" label={`Sent ${analytics.sentCount}`} color="primary" variant="outlined" />
                                <Chip size="small" label={`Opened ${analytics.openedMessages}`} color="success" variant="outlined" />
                                <Chip size="small" label={`Replies ${analytics.replyCount}`} color="warning" variant="outlined" />
                                <Chip size="small" label={`Inbox ${analytics.inboxCount}`} variant="outlined" />
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                Last sent: {formatShortDateTime(analytics.lastSentAt)} • Last open: {formatShortDateTime(analytics.lastOpenAt)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Last reply: {formatShortDateTime(analytics.lastReplyAt)} • Remaining today: {analytics.remainingToday === null ? 'Unlimited' : analytics.remainingToday}
                              </Typography>
                              {analytics.recentActivity.length > 0 ? (
                                <Stack spacing={0.75}>
                                  {analytics.recentActivity.map((activity) => (
                                    <Box key={activity.id} sx={{ p: 1, borderRadius: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                                      <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                                        {activity.type}
                                      </Typography>
                                      <Typography variant="body2" fontWeight={600} noWrap>
                                        {activity.subject}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" noWrap>
                                        {activity.recipient || 'No recipient'} • {formatShortDateTime(activity.at)}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Stack>
                              ) : (
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                  Is account par abhi tracked activity available nahi hai.
                                </Alert>
                              )}
                            </Stack>
                          </Paper>
                          {/* Action Buttons */}
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Refresh />}
                              onClick={() => onTestAccount(account.id)}
                              sx={{ 
                                flex: '1 1 auto',
                                minWidth: 'calc(50% - 4px)',
                                borderColor: '#667eea',
                                color: '#667eea',
                                '&:hover': {
                                  borderColor: '#5568d3',
                                  bgcolor: '#667eea10',
                                },
                              }}
                            >
                              Test
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Edit />}
                              onClick={() => onEditAccount(account)}
                              sx={{ 
                                flex: '1 1 auto',
                                minWidth: 'calc(50% - 4px)',
                                borderColor: '#3b82f6',
                                color: '#3b82f6',
                                '&:hover': {
                                  borderColor: '#2563eb',
                                  bgcolor: '#3b82f610',
                                },
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Delete />}
                              onClick={() => onDeleteAccount(account.id)}
                              sx={{ 
                                flex: '1 1 auto',
                                minWidth: 'calc(50% - 4px)',
                                borderColor: '#dc2626',
                                color: '#dc2626',
                                '&:hover': {
                                  borderColor: '#b91c1c',
                                  bgcolor: '#dc262610',
                                },
                              }}
                            >
                              Delete
                            </Button>
                            
                            {/* Warmup Control Button */}
                            {warmup.status === 'not_started' && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<LocalFireDepartment />}
                                onClick={() => handleInitializeWarmup(account.id)}
                                fullWidth
                                sx={{ 
                                  background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                                  },
                                }}
                              >
                                Start Warmup
                              </Button>
                            )}
                            {warmup.status === 'active' && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Pause />}
                                onClick={() => handlePauseWarmup(account.id)}
                                fullWidth
                                sx={{ 
                                  borderColor: '#f59e0b',
                                  color: '#f59e0b',
                                  '&:hover': {
                                    borderColor: '#d97706',
                                    bgcolor: '#f59e0b10',
                                  },
                                }}
                              >
                                Pause Warmup
                              </Button>
                            )}
                            {warmup.status === 'paused' && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PlayArrow />}
                                onClick={() => handleResumeWarmup(account.id)}
                                fullWidth
                                sx={{ 
                                  borderColor: '#16a34a',
                                  color: '#16a34a',
                                  '&:hover': {
                                    borderColor: '#15803d',
                                    bgcolor: '#16a34a10',
                                  },
                                }}
                              >
                                Resume Warmup
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      </Collapse>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </>
      )}

      {/* Modern Warmup Confirmation Dialog */}
      <Dialog
        open={warmupDialogOpen}
        onClose={() => setWarmupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff',
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(249, 115, 22, 0.3)' : 'rgba(249, 115, 22, 0.2)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            pb: 2,
            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
            color: 'white',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.25)', width: 48, height: 48 }}>
              <LocalFireDepartment sx={{ color: 'white', fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                Start Email Warmup?
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.95)' }}>
                Gradually build your sender reputation
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff' }}>
          <Stack spacing={2.5}>
            <Alert 
              severity="info"
              sx={{ 
                borderRadius: 2,
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'rgba(59, 130, 246, 0.15)' 
                  : 'rgba(59, 130, 246, 0.08)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                '& .MuiAlert-message': {
                  color: theme.palette.mode === 'dark' ? '#e0e7ff' : 'inherit',
                },
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                Email warmup gradually increases your sending volume over 14-30 days to build sender reputation and prevent spam folder delivery.
              </Typography>
            </Alert>
            
            <Paper
              elevation={0}
              sx={{ 
                p: 2.5,
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgba(249, 115, 22, 0.12)'
                  : 'rgba(249, 115, 22, 0.05)',
                borderRadius: 2,
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark' 
                  ? 'rgba(249, 115, 22, 0.3)'
                  : 'rgba(249, 115, 22, 0.2)',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <LocalFireDepartment sx={{ color: '#f97316' }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#f97316' }}>
                  Warmup Schedule
                </Typography>
              </Stack>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.mode === 'dark' ? '#f9731630' : '#f9731620', width: 32, height: 32 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#f97316' }}>
                      1-3
                    </Typography>
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      5 emails/day
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      3 hour intervals
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.mode === 'dark' ? '#f9731630' : '#f9731620', width: 32, height: 32 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#f97316' }}>
                      4-7
                    </Typography>
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      10 emails/day
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      2 hour intervals
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.mode === 'dark' ? '#f9731630' : '#f9731620', width: 32, height: 32 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#f97316' }}>
                      8-14
                    </Typography>
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      20 emails/day
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      1 hour intervals
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.mode === 'dark' ? '#f9731630' : '#f9731620', width: 32, height: 32 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#f97316' }}>
                      15-21
                    </Typography>
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      35 emails/day
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      40 min intervals
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.mode === 'dark' ? '#f9731630' : '#f9731620', width: 32, height: 32 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#f97316' }}>
                      22-30
                    </Typography>
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      50 emails/day
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      30 min intervals
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Paper>

            <Alert 
              severity="warning"
              sx={{ 
                borderRadius: 2,
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgba(245, 158, 11, 0.15)'
                  : 'rgba(245, 158, 11, 0.08)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                '& .MuiAlert-message': {
                  color: theme.palette.mode === 'dark' ? '#fef3c7' : 'inherit',
                },
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                ⚠️ Important
              </Typography>
              <Typography variant="caption">
                Warmup runs automatically in the background, even when you're logged out. 
                Emails will be sent to internal warmup addresses during business hours (9 AM - 6 PM).
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button 
            onClick={() => setWarmupDialogOpen(false)} 
            variant="outlined"
            sx={{
              borderColor: 'rgba(0,0,0,0.23)',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'rgba(0,0,0,0.4)',
                bgcolor: 'rgba(0,0,0,0.04)',
              },
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmInitializeWarmup} 
            variant="contained" 
            startIcon={warmupLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <LocalFireDepartment />}
            disabled={warmupLoading}
            sx={{ 
              px: 3,
              background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
              boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                boxShadow: '0 6px 16px rgba(249, 115, 22, 0.4)',
              },
            }}
          >
            {warmupLoading ? 'Starting...' : 'Start Warmup'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={limitDialogOpen} onClose={() => setLimitDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Sending Limit</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.08) 100%)',
              }}
            >
              <Typography fontWeight={700} variant="body2">
                {limitDialogAccount?.label || limitDialogAccount?.fromEmail || limitDialogAccount?.username || 'Account'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {limitDialogAccount?.fromEmail || limitDialogAccount?.username || ''}
              </Typography>
            </Paper>
            <TextField
              autoFocus
              fullWidth
              type="number"
              label="Daily sending limit"
              value={limitDialogAccount ? getLimitDraftValue(limitDialogAccount) : ''}
              onChange={(e) => {
                if (!limitDialogAccount) return;
                setLimitDrafts((prev) => ({ ...prev, [limitDialogAccount.id]: e.target.value }));
              }}
              helperText="Set how many emails this account can send per day."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleLimitDialogSave}>Save Limit</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountsManager;
